// Customer model
// Importing from types/customer.ts
import { Customer as CustomerType } from '../types/customer';
// استيراد نموذج التخفيض
import { ProductDiscount } from '../types/promotion';
// استيراد أنواع تخزين الصور
import { ImageStorageType, PreferredDisplaySource } from '../services/imageStorageService';
// نموذج الديون - مستورد من utils/debtTypes.ts
export type { Debt, DebtPayment } from './debtTypes';

export type Customer = CustomerType;

// Device sizes
export type DeviceSize = 'small' | 'medium' | 'large' | 'extraLarge';

// App theme modes
export type ThemeMode = 'light' | 'dark';

// Device types
export type DeviceType =
  'هاتف ذكي' |
  'جهاز لوحي (تابلت)' |
  'كمبيوتر محمول' |
  'كمبيوتر مكتبي' |
  'طابعة' |
  'شاشة' |
  'جهاز ألعاب' |
  'مكيف هواء' |
  'ثلاجة' |
  'غسالة' |
  'تلفاز' |
  'أخرى';

// Maintenance status
export enum MaintenanceStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  RETURNED = 'returned' // مرتجع - الجهاز عاد للعميل مع رفض الإصلاح
}

// Payment status
export type PaymentStatus = 'unpaid' | 'partiallyPaid' | 'fullyPaid' | 'paid' | 'partial';

// Payment method (for maintenance)
export type MaintenancePaymentMethod = 'cash' | 'debt' | 'partial' | 'vodafone_wallet' | 'instapay' | 'etisalat_wallet';

// أسباب إرجاع الصيانة
export enum MaintenanceReturnReason {
  SAME_ISSUE = 'same_issue', // نفس العيب
  NEW_ISSUE = 'new_issue', // عيب جديد
  CUSTOMER_REFUSAL = 'customer_refusal', // رفض العميل للإصلاح
  COST_TOO_HIGH = 'cost_too_high', // التكلفة عالية
  PART_NOT_AVAILABLE = 'part_not_available', // قطعة غير متوفرة
  DEVICE_BEYOND_REPAIR = 'device_beyond_repair', // الجهاز غير قابل للإصلاح
  OTHER = 'other' // أخرى
}

// Payment method (for general use)
export type PaymentMethod = 'cash' | 'card' | 'bankTransfer' | 'electronicPayment' | 'cod' | 'stc' | 'mada';

// Cost stages
export type CostStage = 'initial' | 'estimated' | 'final';

// Payment transaction interface
export interface PaymentTransaction {
  id: string;
  maintenanceId: string;
  amount: number;
  method: MaintenancePaymentMethod;
  date: Date | string;
  notes?: string;
  createdBy: string;
  createdByName: string;
}

// عنصر الإرجاع
export interface ReturnItem {
  itemId: string;
  name: string;
  sku: string;
  quantity: number;
  price: number;
  total: number;
  warehouseId: string;
  returnReason: string;
  returnQuantity?: number; // الكمية المرتجعة (للواجهة)
}

// نوع سجل الإرجاع
export interface ReturnRecord {
  id: string;
  saleId: string;
  customerId?: string;
  customerName: string;
  employeeId: string;
  employeeName: string;
  branchId: string;
  warehouseId: string;
  items: ReturnItem[];
  totalAmount: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date | string;
}

// نموذج نوع الجهاز
export interface DeviceTypeModel {
  id: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// أنواع أجور الفنيين
export enum TechnicianSalaryType {
  FIXED = 'fixed',           // راتب ثابت
  PER_DEVICE = 'per_device', // بالقطعة
  PERCENTAGE = 'percentage', // بالنسبة المئوية
  VARIABLE = 'variable'      // بالقطعة مع قيمة متغيرة
}

// نموذج الفني
export interface Technician {
  id: string;
  name: string;
  phone: string;
  email?: string;
  specialization: string; // التخصص (هواتف، لابتوب، إلخ)
  salaryType: TechnicianSalaryType;
  salaryAmount: number; // المبلغ الثابت أو المبلغ لكل قطعة أو النسبة المئوية
  isActive: boolean;
  branchId: string;
  branchName: string;
  hireDate: Date | string;
  notes?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// نموذج أجر الفني لطلب صيانة محدد
export interface TechnicianEarning {
  id: string;
  technicianId: string;
  technicianName: string;
  maintenanceId: string;
  deviceType: string;
  deviceSize?: string;
  salaryType: TechnicianSalaryType;
  baseAmount: number; // المبلغ الأساسي (تكلفة الصيانة أو المبلغ الثابت)
  earningAmount: number; // المبلغ المكتسب
  calculationDetails: string; // تفاصيل الحساب
  date: Date | string;
  branchId: string;
  createdAt: Date | string;
}

// إحصائيات الفني
export interface TechnicianStats {
  technicianId: string;
  technicianName: string;
  totalDevices: number;
  totalEarnings: number;
  averageEarningPerDevice: number;
  devicesByType: Record<string, number>;
  earningsByDate: Record<string, number>;
  period: {
    startDate: Date;
    endDate: Date;
  };
}

// نموذج حجم الجهاز
export interface DeviceSizeModel {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// نموذج ملحق الجهاز
export interface DeviceAccessoryModel {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// نموذج ماركة الجهاز
export interface DeviceBrandModel {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// Device model
export interface Device {
  id: string;
  customerId: string;
  type: DeviceType;
  typeId?: string;         // مرجع لنوع الجهاز
  brand?: string;
  brandId?: string;        // مرجع لماركة الجهاز
  model?: string;
  serialNumber?: string;
  size?: DeviceSize;
  sizeId?: string;         // مرجع لحجم الجهاز
  purchaseDate?: Date | string;
  notes?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// Device in maintenance record
export interface MaintenanceDevice {
  id: string; // معرف فريد للجهاز في هذا الطلب
  deviceId: string; // معرف الجهاز في قاعدة البيانات
  deviceType: DeviceType;
  brand: string;
  model: string;
  serialNumber?: string;
  size?: DeviceSize;
  issue: string;
  diagnosis?: string;
  accessories?: string[];
  usedParts: InventoryItem[];
  status: MaintenanceStatus;
  completedAt?: Date | string;
  deliveredAt?: Date | string;
  notes?: string;
}

// Maintenance record model
export interface MaintenanceRecord {
  id: string;
  orderNumber: number; // Número secuencial para facilitar la identificación
  customerId: string;
  customerName: string;
  customerPhone?: string;
  devices: MaintenanceDevice[]; // مصفوفة الأجهزة
  receivedAt: Date | string;
  estimatedDeliveryDate?: Date | string;
  actualDeliveryDate?: Date | string;
  completedAt?: Date | string;
  deliveredAt?: Date | string;
  receivedByEmployeeId?: string;
  receivedByEmployeeName?: string;
  technicianId?: string; // معرف الفني المسؤول
  technicianName?: string; // اسم الفني المسؤول
  branchId: string; // الفرع الذي تم استلام الجهاز فيه
  branchName: string; // اسم الفرع
  warehouseId: string; // المخزن المرتبط بهذه الصيانة
  warehouseName: string; // اسم المخزن
  status: MaintenanceStatus; // الحالة العامة للطلب

  // الحقول التالية للتوافق مع النظام الحالي (سيتم حسابها من الأجهزة)
  deviceId: string; // معرف الجهاز الأول
  deviceType: DeviceType; // نوع الجهاز الأول
  issue: string; // مشكلة الجهاز الأول
  diagnosis?: string;
  accessories?: string[];
  usedParts: InventoryItem[];

  // نظام التكلفة الجديد
  initialCost: number; // التكلفة المبدئية عند الاستلام
  estimatedCost?: number; // التكلفة المقدرة بعد الفحص
  finalCost?: number; // التكلفة النهائية عند التسليم
  partsCost?: number; // تكلفة قطع الغيار المستخدمة
  laborCost?: number; // تكلفة العمالة (finalCost - partsCost)

  // نظام الدفع الجديد
  totalPaid: number; // إجمالي المبلغ المدفوع
  remainingAmount: number; // المبلغ المتبقي
  paymentStatus: PaymentStatus;
  paymentMethod?: MaintenancePaymentMethod; // طريقة الدفع النهائية
  paymentDate?: Date | string; // تاريخ آخر دفعة

  // إدارة الديون
  hasDebt: boolean; // هل يوجد دين متبقي
  debtAmount?: number; // مبلغ الدين
  debtDueDate?: Date | string; // تاريخ استحقاق الدين
  debtId?: string; // معرف سجل الدين

  // خيارات العميل
  requireCallBeforeMaintenance: boolean; // طلب الاتصال قبل الصيانة
  customerNotified: boolean; // تم إشعار العميل بالتكلفة
  customerApproved: boolean; // وافق العميل على التكلفة

  // للتوافق مع النظام القديم (deprecated)
  paid: number; // سيتم استبداله بـ totalPaid
  maintenanceFee: number; // سيتم استبداله بـ laborCost
  technicianShare: number; // سيتم حسابه من laborCost
  costAfterContact?: number; // سيتم استبداله بـ estimatedCost

  notes?: string;
  images?: MaintenanceImage[]; // صور الجهاز والعيوب
  lastUrgentMessageSentAt?: Date | string; // آخر مرة تم إرسال رسالة استعجال فيها
  
  // بيانات الضمان
  warrantyNumber?: string; // رقم إيصال الضمان
  warrantyIssueDate?: Date | string; // تاريخ إصدار الضمان
  warrantyExpiryDate?: Date | string; // تاريخ انتهاء الضمان
}

// واجهة صورة الصيانة
export interface MaintenanceImage {
  id: string;
  type: 'serial' | 'model' | 'defect';
  label: string;
  url: string;
  uploadedUrl?: string;
  timestamp: Date | string;
  provider?: 'imgbb' | 'imagekit' | 'cloudinary';
  metadata?: {
    maintenanceId: string;
    imageType: string;
    timestamp: string;
  };
}

// نوع للكمية في مخزن محدد
export interface WarehouseQuantity {
  warehouseId: string;
  warehouseName: string;
  quantity: number;
  location?: string;
  branchId?: string;
  branchName?: string;
}

// تعديل واجهة عنصر المخزون
export interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  barcode?: string;
  category: string;
  categoryId?: string;
  subcategory?: string;
  subcategoryId?: string;
  purchasePrice: number;
  sellingPrice: number;
  unitPrice?: number; // سعر الوحدة
  // أسعار متعددة للمنتج
  posPrice?: number; // سعر نقطة البيع (POS)
  storePrice?: number; // سعر المتجر الإلكتروني
  wholesalePrice?: number; // سعر الجملة
  quantity: number; // الكمية الإجمالية في جميع المخازن
  minQuantity: number;
  description?: string;
  supplier?: string;
  warehouseId?: string; // المخزن الافتراضي
  warehouseName?: string;
  warehouseQuantities?: WarehouseQuantity[]; // الكميات في المخازن المختلفة
  totalQuantity?: number; // مجموع جميع الكميات (للتأكد من التوافق في المستقبل)
  image?: string; // Optional: Base64 or local placeholder
  imageUrl?: string; // URL to the image (from any service)
  imgbbUrl?: string;  // URL from ImgBB
  imagekitUrl?: string; // URL from ImageKit.io
  additionalImages?: ProductImage[]; // مصفوفة من الصور الإضافية للمنتج
  imageFile?: File;
  showInStore?: boolean;
  featured?: boolean; // منتج مميز
  bestSeller?: boolean; // منتج الأكثر مبيعًا
  onSale?: boolean; // منتج عليه تخفيض
  discount?: ProductDiscount; // تفاصيل التخفيض
  originalPrice?: number; // السعر الأصلي قبل التخفيض
  lastRestockDate?: Date;
  createdAt: Date | string;
  updatedAt: Date | string;
  isLowStock?: boolean;
  location?: string;
}

// Extended inventory item for display
export interface ExtendedInventoryItem extends InventoryItem {
  isLowStock?: boolean;
}

// Inventory transaction model
export interface InventoryTransaction {
  id: string;
  itemId: string;
  itemName: string;
  itemSku?: string;
  quantity: number;
  type: TransactionType;
  fromWarehouseId?: string;
  fromWarehouseName?: string;
  toWarehouseId?: string;
  toWarehouseName?: string;
  createdById?: string;
  createdByName?: string;
  notes?: string;
  timestamp: Date | string;
}

// نوع معاملة المخزون
export enum TransactionType {
  PURCHASE = 'purchase',
  SALE = 'sale',
  ADJUSTMENT = 'adjustment',
  TRANSFER = 'transfer',
  RESTOCK = 'restock'
}

// واجهة صورة المنتج
export interface ProductImage {
  id: string;           // معرف الصورة
  url: string;          // رابط الصورة
  imgbbUrl?: string;    // رابط ImgBB (إذا تم استخدامه)
  imagekitUrl?: string; // رابط ImageKit.io (إذا تم استخدامه)
  localUrl?: string;    // رابط محلي (إذا تم استخدامه)
  isMain?: boolean;     // هل هي الصورة الرئيسية
  sortOrder?: number;   // ترتيب الصورة
  caption?: string;     // وصف الصورة
  createdAt: Date | string; // تاريخ الإنشاء
}

// Sale model
export interface Sale {
  id: string;
  invoiceNumber?: string; // رقم الفاتورة التسلسلي
  customerName?: string;
  customerId?: string;
  customerPhone?: string; // رقم هاتف العميل
  items: SaleItem[];
  total: number;
  discount: number;
  tax?: number;
  grandTotal: number;
  currency?: string; // عملة البيع (للمبيعات المستقبلية)
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  amountPaid?: number;
  remainingAmount?: number; // المبلغ المتبقي للدفع
  isDebt?: boolean; // هل هذه عملية بيع بالدين
  dueDate?: Date | string; // تاريخ استحقاق الدفع
  employeeId: string;
  employeeName: string;
  branchId: string; // الفرع الذي تمت فيه عملية البيع
  branchName: string; // اسم الفرع
  warehouseId: string; // المخزن الذي تم سحب المنتجات منه
  warehouseName: string; // اسم المخزن
  saleDate?: Date | string;
  createdAt: Date | string;
  receiptPrinted?: boolean;
  isPrinted?: boolean;
  notes?: string;
  // حقول المرتجعات
  returnStatus?: 'FULL_RETURNED' | 'PARTIAL_RETURNED';
  returns?: string[]; // معرفات المرتجعات
  lastReturnDate?: Date | string;
}

// Sale item model
export interface SaleItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  warehouseId: string;
  sku?: string;
  warehouseName?: string;
}

// واجهة المستخدم/الموظف
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  branchId: string;
  branchName?: string;
  isActive: boolean;
  permissions: UserPermissions;
  createdAt: Date;
  updatedAt: Date;
  allowedWarehouses?: string[];
  addresses?: CustomerAddress[];
}

import { CustomerAddress } from '../types/store';

// واجهة بيانات المستخدم للعرض
export interface UserData extends User {
  branchName: string;
}

// صلاحيات المستخدم
export interface UserPermissions {
  // صلاحيات الوصول للصفحات
  canAccessDashboard: boolean;
  canAccessInventory: boolean;
  canAccessPOS: boolean;
  canAccessMaintenance: boolean;
  canAccessCustomers: boolean;
  canAccessSettings: boolean;
  canAccessReports: boolean;
  canModifyInventory: boolean;
  canAccessDebts: boolean;
  canViewExpenses: boolean;
  canViewBasicExpenses: boolean;
  canViewDailyExpenses: boolean;
  canAddExpenses: boolean;
  canEditExpenses: boolean;
  canDeleteExpenses: boolean;
  canManageBackup: boolean;

  // صلاحيات المخزون
  canViewInventory: boolean;
  canAddInventory: boolean;
  canEditInventory: boolean;
  canDeleteInventory: boolean;
  canTransferInventory: boolean;

  // صلاحيات المبيعات
  canViewSales: boolean;
  canCreateSales: boolean;
  canEditSales: boolean;
  canDeleteSales: boolean;
  canApplyDiscount: boolean;

  // صلاحيات الصيانة
  canViewMaintenance: boolean;
  canCreateMaintenance: boolean;
  canEditMaintenance: boolean;
  canDeleteMaintenance: boolean;
  canAssignTechnician: boolean;
  canChangeMaintStatus: boolean;

  // صلاحيات العملاء
  canViewCustomers: boolean;
  canAddCustomers: boolean;
  canEditCustomers: boolean;
  canDeleteCustomers: boolean;

  // صلاحيات الفروع والمخازن
  canManageBranches: boolean;
  canManageWarehouses: boolean;

  // صلاحيات المستخدمين
  canManageUsers: boolean;
  canViewUsers: boolean;

  // صلاحيات الإعدادات
  canManageSettings: boolean;
}

// دور المستخدم/الموظف
export enum UserRole {
  ADMIN = 'ADMIN',
  BRANCH_MANAGER = 'BRANCH_MANAGER',
  WAREHOUSE_MANAGER = 'WAREHOUSE_MANAGER',
  TECHNICIAN = 'TECHNICIAN',
  CASHIER = 'CASHIER',
  INVENTORY_CLERK = 'INVENTORY_CLERK',
  SALES = 'SALES',
  CUSTOMER = 'CUSTOMER' // دور العميل في المتجر
}

// واجهة الفرع
export interface Branch {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  warehouseIds: string[];
  defaultWarehouseId?: string;
  allowedEmployees?: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Warehouse model
export interface Warehouse {
  id: string;
  name: string;
  location?: string;
  branchId?: string; // الفرع المرتبط بهذا المخزن
  branchName?: string; // اسم الفرع
  isMain?: boolean; // هل هذا المخزن رئيسي
  inventory?: number; // إجمالي قيمة المخزون
  itemCount?: number; // عدد الأصناف
  createdAt: Date | string;
  updatedAt: Date | string;
}



// Shipping company model
export interface ShippingCompany {
  id: string;
  name: string;
  logo?: string;
  estimatedDeliveryTime: string;
  shippingCosts: ShippingCost[];
  trackingUrl?: string;
}

// Shipping cost model
export interface ShippingCost {
  id: string;
  region: string;
  cost: number;
}

// Print type for receipts
export type PrintType = 'none' | 'barcode' | 'receipt' | 'invoice';

// نموذج الفئات
export interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  imageUrl?: string;
  isActive: boolean;
  order?: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  subCategories?: Category[];
}

// نموذج الفئات الفرعية (يرث من Category)
export interface SubCategory extends Category {
  parentId: string;
  parentName?: string;
}



// نوع المرتجع
export enum ReturnType {
  FULL = 'FULL',      // مرتجع كامل للفاتورة
  PARTIAL = 'PARTIAL'  // مرتجع جزئي للفاتورة
}

// حالة المرتجع
export enum ReturnStatus {
  COMPLETED = 'COMPLETED',  // تم إكمال المرتجع
  PENDING = 'PENDING',      // قيد الانتظار
  CANCELLED = 'CANCELLED'   // تم إلغاء المرتجع
}

// طريقة الاسترداد
export enum RefundMethod {
  CASH = 'cash',           // نقدي
  CREDIT = 'credit'        // رصيد للعميل
}

// واجهة المرتجع
export interface SaleReturn {
  id: string;
  saleId: string;          // معرف الفاتورة الأصلية
  returnType: ReturnType;  // نوع المرتجع
  returnStatus: ReturnStatus; // حالة المرتجع
  items: ReturnItem[];     // العناصر المرتجعة
  totalAmount: number;     // إجمالي مبلغ المرتجع
  returnReason?: string;   // سبب الإرجاع
  refundMethod: RefundMethod; // طريقة الاسترداد (نقدي أو رصيد)
  employeeId: string;      // معرف الموظف الذي قام بالمرتجع
  employeeName: string;    // اسم الموظف
  branchId: string;        // معرف الفرع
  branchName: string;      // اسم الفرع
  warehouseId: string;     // معرف المخزن
  warehouseName: string;   // اسم المخزن
  customerId?: string;     // معرف العميل
  customerName?: string;   // اسم العميل
  createdAt: Date | string; // تاريخ الإنشاء
  notes?: string;          // ملاحظات
}

// تم نقل تعريف Customer إلى src/types/customer.ts

/**
 * أنواع حماية الصور
 */
export enum ImageProtectionType {
  NONE = 'none',                   // بدون حماية
  PREVENT_SAVE = 'prevent_save',   // منع الحفظ فقط
  PREVENT_OPEN = 'prevent_open',   // منع الفتح في صفحة مستقلة
  FULL = 'full'                    // حماية كاملة (منع الحفظ والفتح)
}

/**
 * واجهة إعدادات تخزين الصور
 */
export interface ImageStorageSettings {
  storageType: ImageStorageType;
  preferredDisplaySource?: PreferredDisplaySource; // Optional: Only relevant for DUAL types
  imageProtection?: ImageProtectionType; // نوع حماية الصور
  watermarkText?: string; // نص العلامة المائية (إذا كانت مفعلة)
  updatedAt?: Date;
}

// ===============================
// أنواع نظام المراسلة والملاحظات
// ===============================

export interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: UserRole;
  content: string;
  type: 'text' | 'image' | 'file' | 'note';
  chatId: string; // معرف المحادثة أو الغرفة
  timestamp: Date;
  isRead: boolean;
  readBy: string[]; // قائمة معرفات المستخدمين الذين قرأوا الرسالة
  attachments?: MessageAttachment[];
  reactions?: MessageReaction[];
  replyTo?: string; // معرف الرسالة المرد عليها
  isEdited: boolean;
  editedAt?: Date;
  isDeleted: boolean;
  deletedAt?: Date;
}

export interface MessageAttachment {
  id: string;
  name: string;
  type: 'image' | 'document' | 'video' | 'audio';
  url: string;
  size: number;
  uploadedAt: Date;
}

export interface MessageReaction {
  userId: string;
  userName: string;
  emoji: string;
  timestamp: Date;
}

export interface Chat {
  id: string;
  name: string;
  type: 'private' | 'group' | 'announcement';
  description?: string;
  participants: ChatParticipant[];
  createdBy: string;
  createdAt: Date;
  lastMessage?: Message;
  lastActivity: Date;
  isActive: boolean;
  settings: ChatSettings;
  avatar?: string;
}

export interface ChatParticipant {
  userId: string;
  userName: string;
  userRole: UserRole;
  joinedAt: Date;
  role: 'admin' | 'moderator' | 'member';
  permissions: ChatPermissions;
  lastSeen: Date;
  isOnline: boolean;
}

export interface ChatPermissions {
  canSendMessages: boolean;
  canSendFiles: boolean;
  canDeleteMessages: boolean;
  canEditChat: boolean;
  canAddMembers: boolean;
  canRemoveMembers: boolean;
}

export interface ChatSettings {
  allowFileSharing: boolean;
  allowReactions: boolean;
  allowReplies: boolean;
  maxFileSize: number; // بالميجابايت
  allowedFileTypes: string[];
  muteNotifications: boolean;
  autoDeleteMessages: boolean;
  autoDeleteDays: number;
}

export interface Note {
  id: string;
  title: string;
  content: string;
  authorId: string;
  authorName: string;
  type: 'personal' | 'shared' | 'announcement';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  attachments?: MessageAttachment[];
  sharedWith: string[]; // معرفات المستخدمين المشاركين
  dueDate?: Date;
  isCompleted: boolean;
  completedAt?: Date;
  completedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  isArchived: boolean;
  archivedAt?: Date;
  comments: NoteComment[];
}

export interface NoteComment {
  id: string;
  noteId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: Date;
  isEdited: boolean;
  editedAt?: Date;
}

export interface TeamNotification {
  id: string;
  userId: string;
  type: 'message' | 'note' | 'mention' | 'system';
  title: string;
  content: string;
  data?: any; // بيانات إضافية حسب نوع الإشعار
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
  actionUrl?: string; // رابط للانتقال إلى المحتوى المرتبط
}

export type MessageType = 'text' | 'image' | 'file' | 'note';
export type ChatType = 'private' | 'group' | 'announcement';
export type NoteType = 'personal' | 'shared' | 'announcement';
export type NotePriority = 'low' | 'medium' | 'high' | 'urgent';
export type NotificationType = 'message' | 'note' | 'mention' | 'system';
export type ChatRole = 'admin' | 'moderator' | 'member';

// واجهة بيانات إرجاع الصيانة
export interface MaintenanceReturn {
  id: string;
  maintenanceId: string;
  returnReason: MaintenanceReturnReason;
  customReturnReason?: string; // سبب مخصص إذا كان السبب "أخرى"
  issueDescription: string; // وصف العيب (نفس العيب أو عيب جديد)
  refundAmount: number; // المبلغ المسترد
  refundMethod: 'cash' | 'debt_reduction'; // طريقة الاسترداد
  deviceReturned: boolean; // هل تم إرجاع الجهاز للعميل
  notes?: string; // ملاحظات إضافية
  employeeId: string;
  employeeName: string;
  customerId: string;
  customerName: string;
  deviceType: string;
  deviceBrand?: string;
  deviceModel?: string;
  createdAt: Date;
  processedAt: Date;
}

// واجهة إيصال الضمان
export interface WarrantyReceipt {
  warrantyNumber: string; // رقم الضمان الفريد
  issueDate: Date; // تاريخ الإصدار
  expiryDate: Date; // تاريخ الانتهاء
  maintenanceId: string; // معرف طلب الصيانة
  orderNumber: number; // رقم الطلب
  
  // بيانات العميل
  customerName: string;
  customerPhone: string;
  customerAddress?: string;
  
  // بيانات الجهاز
  deviceType: string;
  deviceBrand?: string;
  deviceModel?: string;
  serialNumber?: string;
  issueDescription: string;
  accessories?: string[]; // الملحقات المستلمة
  
  // البيانات المالية
  initialCost: number; // التكلفة المبدئية
  finalCost?: number; // التكلفة النهائية (إن تم تحديدها)
  totalPaid: number; // المبلغ المدفوع
  remainingAmount: number; // المبلغ المتبقي
  paymentStatus: PaymentStatus;
  
  // شروط الضمان
  warrantyTerms: string[]; // قائمة شروط الضمان
  warrantyDurationDays: number; // مدة الضمان بالأيام
  
  // معلومات الموظف
  technicianName: string;
  technicianId: string;
  receivedByName: string;
  receivedById: string;
  
  // معلومات الشركة
  companyName: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogo?: string;
  
  // QR Code
  qrCodeData: string; // البيانات المشفرة في QR Code
  verificationUrl: string; // رابط التحقق
  
  // الفرع والمخزن
  branchName: string;
  warehouseName?: string;
  
  // Footer
  footerMessage?: string;
  printedAt: Date; // وقت الطباعة
}